/**
 * Subscriptions portal web component.
 *
 * Renders each plan as a card with a nested list of items. Plan-level
 * actions live on the card header (skip, pause, resume, change frequency,
 * update card, cancel). Item-level actions live on each item row (change
 * quantity, remove).
 */

import { Component } from '@theme/component';
import {
  SubscriptionsClient,
  showToast,
  describeError,
} from './subscriptions-client-v4.js';
import {
  pauseDialog,
  frequencyDialog,
  quantityDialog,
  cancelDialog,
} from './subscriptions-dialog.js';

const FREQUENCY_LABELS = {
  biweekly: 'Every 2 weeks',
  monthly: 'Every month',
  bimonthly: 'Every 2 months',
  quarterly: 'Every 3 months',
};

const STATUS_LABELS = {
  active: 'Active',
  paused: 'Paused',
  past_due: 'Past due',
  cancelled: 'Cancelled',
};

class SubscriptionsPortalComponent extends Component {
  requiredRefs = [
    'loading',
    'error',
    'errorMessage',
    'empty',
    'list',
    'planTemplate',
    'itemTemplate',
  ];

  #client = new SubscriptionsClient();

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  retry() {
    this.#load();
  }

  async #load() {
    this.#setState('loading');
    try {
      const { plans } = await this.#client.list();
      if (!plans || plans.length === 0) {
        this.#setState('empty');
        return;
      }
      this.#renderPlans(plans);
      this.#setState('list');
    } catch (err) {
      this.#setError(err);
    }
  }

  #setState(state) {
    const map = {
      loading: this.refs.loading,
      error: this.refs.error,
      empty: this.refs.empty,
      list: this.refs.list,
    };
    for (const [name, el] of Object.entries(map)) {
      if (!el) continue;
      el.hidden = name !== state;
    }
  }

  #setError(err) {
    if (this.refs.errorMessage) {
      this.refs.errorMessage.textContent = describeError(err);
    }
    this.#setState('error');
  }

  #renderPlans(plans) {
    const list = this.refs.list;
    if (!list) return;
    list.innerHTML = '';
    for (const plan of plans) {
      list.appendChild(this.#renderPlan(plan));
    }
  }

  #renderPlan(plan) {
    const tpl = this.refs.planTemplate;
    const frag = /** @type {DocumentFragment} */ (tpl.content.cloneNode(true));
    const card = /** @type {HTMLElement} */ (frag.querySelector('.sub-plan'));
    card.dataset.planId = plan.id;

    const statusEl = card.querySelector('[data-status]');
    if (statusEl) {
      statusEl.textContent = STATUS_LABELS[plan.status] ?? plan.status;
      statusEl.setAttribute('data-status-variant', plan.status);
    }
    this.#setText(
      card,
      '[data-frequency]',
      FREQUENCY_LABELS[plan.frequency] ?? plan.frequency,
    );
    this.#setText(
      card,
      '[data-next-renewal]',
      plan.status === 'paused'
        ? `Paused until ${this.#formatDate(plan.pausedUntil)}`
        : plan.status === 'cancelled'
        ? 'Cancelled'
        : this.#formatDate(plan.nextRenewalAt),
    );
    this.#setText(
      card,
      '[data-renewal-subtotal]',
      this.#formatMoney(plan.renewalSubtotalCents),
    );
    this.#setText(card, '[data-item-count]', String(plan.items.length));

    this.#wirePlanActions(card, plan);

    const itemsContainer = card.querySelector('[data-items]');
    if (itemsContainer) {
      for (const item of plan.items) {
        itemsContainer.appendChild(this.#renderItem(plan, item));
      }
    }

    return frag;
  }

  #wirePlanActions(card, plan) {
    const pick = (action) =>
      card.querySelector(`[data-plan-action="${action}"]`);
    const hideIfNotActive = (btn) => {
      if (btn && plan.status !== 'active') btn.hidden = true;
    };
    const hideIfCancelled = (btn) => {
      if (btn && plan.status === 'cancelled') btn.hidden = true;
    };

    const skipBtn = pick('skip');
    hideIfNotActive(skipBtn);
    skipBtn?.addEventListener('click', () =>
      this.#safe(async () => {
        const r = await this.#client.skipNext(plan.id);
        showToast(
          `Next renewal moved to ${this.#formatDate(r.nextRenewalAt)}.`,
          { variant: 'success' },
        );
        await this.#load();
      }),
    );

    const pauseBtn = pick('pause');
    hideIfNotActive(pauseBtn);
    pauseBtn?.addEventListener('click', () => this.#handlePause(plan));

    const resumeBtn = pick('resume');
    if (resumeBtn) {
      resumeBtn.hidden = plan.status !== 'paused';
      resumeBtn.addEventListener('click', () =>
        this.#safe(async () => {
          await this.#client.resume(plan.id);
          showToast('Plan resumed.', { variant: 'success' });
          await this.#load();
        }),
      );
    }

    const freqBtn = pick('frequency');
    hideIfNotActive(freqBtn);
    freqBtn?.addEventListener('click', () => this.#handleFrequency(plan));

    const cardBtn = pick('update-payment');
    hideIfCancelled(cardBtn);
    cardBtn?.addEventListener('click', () =>
      this.#handleUpdatePayment(plan),
    );

    const cancelBtn = pick('cancel');
    hideIfCancelled(cancelBtn);
    cancelBtn?.addEventListener('click', () => this.#handleCancel(plan));
  }

  #renderItem(plan, item) {
    const tpl = this.refs.itemTemplate;
    const frag = /** @type {DocumentFragment} */ (tpl.content.cloneNode(true));
    const row = /** @type {HTMLElement} */ (frag.querySelector('.sub-item'));
    row.dataset.itemId = item.id;

    this.#setText(row, '[data-item-title]', item.productTitle);
    this.#setText(row, '[data-item-variant]', item.variantTitle ?? '');
    this.#setText(row, '[data-item-qty]', `×${item.quantity}`);
    this.#setText(
      row,
      '[data-item-unit]',
      this.#formatMoney(item.renewalUnitCents),
    );
    this.#setText(
      row,
      '[data-item-subtotal]',
      this.#formatMoney(item.lineSubtotalCents),
    );

    const savings =
      item.volumeTierPercent > 0
        ? `Sub ${item.subDiscountPercent}% + volume ${item.volumeTierPercent}%`
        : `Sub ${item.subDiscountPercent}%`;
    this.#setText(row, '[data-item-discount]', savings);

    const disableAction = plan.status === 'cancelled';

    const qtyBtn = row.querySelector('[data-item-action="quantity"]');
    if (qtyBtn) {
      qtyBtn.hidden = disableAction;
      qtyBtn.addEventListener('click', () =>
        this.#handleItemQuantity(plan, item),
      );
    }
    const rmBtn = row.querySelector('[data-item-action="remove"]');
    if (rmBtn) {
      rmBtn.hidden = disableAction;
      rmBtn.addEventListener('click', () => this.#handleItemRemove(plan, item));
    }

    return frag;
  }

  // ---- Plan-level handlers --------------------------------------------

  async #handlePause(plan) {
    const untilIso = await pauseDialog();
    if (!untilIso) return;
    await this.#safe(async () => {
      await this.#client.pause(plan.id, untilIso);
      showToast(`Paused until ${this.#formatDate(untilIso)}.`, {
        variant: 'success',
      });
      await this.#load();
    });
  }

  async #handleFrequency(plan) {
    const next = await frequencyDialog(plan.frequency);
    if (!next) return;
    await this.#safe(async () => {
      await this.#client.changeFrequency(plan.id, /** @type {any} */ (next));
      showToast('Frequency updated.', { variant: 'success' });
      await this.#load();
    });
  }

  async #handleUpdatePayment(plan) {
    const collectKey = this.dataset.collectJsKey;
    if (!collectKey) {
      showToast('Card update is not configured. Please contact support.', {
        variant: 'error',
      });
      return;
    }
    window.location.href = `/pages/subscription-update-card?id=${encodeURIComponent(plan.id)}`;
  }

  async #handleCancel(plan) {
    const result = await cancelDialog({
      scope: 'plan',
      body: 'All items in this plan will stop renewing. You can always restart from a product page.',
    });
    if (!result) return;
    await this.#safe(async () => {
      await this.#client.cancel(plan.id, result.reason ?? undefined);
      showToast('Plan cancelled.', { variant: 'success' });
      await this.#load();
    });
  }

  // ---- Item-level handlers --------------------------------------------

  async #handleItemQuantity(plan, item) {
    const next = await quantityDialog({
      current: item.quantity,
      min: 1,
      max: 10,
      productTitle: item.productTitle,
    });
    if (!next) return;
    await this.#safe(async () => {
      await this.#client.changeItemQuantity(plan.id, item.id, next);
      showToast('Quantity updated.', { variant: 'success' });
      await this.#load();
    });
  }

  async #handleItemRemove(plan, item) {
    const result = await cancelDialog({
      scope: 'item',
      body: `Remove "${item.productTitle}" from this plan? Other items keep renewing.`,
    });
    if (!result) return;
    await this.#safe(async () => {
      await this.#client.removeItem(plan.id, item.id);
      showToast('Item removed.', { variant: 'success' });
      await this.#load();
    });
  }

  // ---- Utilities ------------------------------------------------------

  async #safe(fn) {
    try {
      await fn();
    } catch (err) {
      showToast(describeError(err), { variant: 'error' });
    }
  }

  #formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '—';
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  #formatDate(iso) {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  }

  #defaultPauseDate() {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + 30);
    return d.toISOString().slice(0, 10);
  }

  #setText(root, sel, text) {
    const el = root.querySelector(sel);
    if (el) el.textContent = text;
  }
}

if (!customElements.get('subscriptions-portal-component')) {
  customElements.define(
    'subscriptions-portal-component',
    SubscriptionsPortalComponent,
  );
}
